import type { AddonInstallation, Meta } from '../../../packages/contracts/src/index.ts';
import { CINEMETA_ID, cinemeta } from '../../../packages/contracts/src/cinemeta.ts';
import { catalogChoices, type CatalogChoice } from '../../../packages/slc-web/src/catalogs.ts';
import { contentMeta } from '../../../packages/slc-web/src/content.ts';
import { fallbackVideos } from '../../../packages/contracts/src/metadata.ts';
import { addonCatalogPage } from './addon-catalog-page.ts';

type Transport = <T>(path: string, options?: RequestInit) => Promise<T>;
type Supports = (addon: AddonInstallation, meta: Pick<Meta, 'id' | 'type'>, signal: AbortSignal) => Promise<boolean>;
function current(signal: AbortSignal) { if (signal.aborted) throw new DOMException('Cancelled', 'AbortError'); }

/** Profile-scoped host transport. Configured addon URLs never enter this client.
 * Closing the owner aborts all work, including the shared installation read. */
export class AddonContent {
  private readonly lifetime = new AbortController();
  private installations?: Promise<AddonInstallation[]>;
  readonly profileId: string;
  private readonly request: Transport;
  private readonly supports: Supports;
  constructor(profileId: string, request: Transport, supports: Supports) {
    if (!profileId || profileId.length > 180 || /[\x00-\x1f/]/.test(profileId)) throw Error('Choose a valid profile.');
    this.profileId=profileId;this.request=request;this.supports=supports;
  }
  close() { this.lifetime.abort(); }
  invalidateAddons() { this.installations=undefined; }
  private async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    // A caller's cancellation must abort transport, including native transports
    // which ignore signal; the post-await check still retires their late result.
    const controller=new AbortController(), inputs=[this.lifetime.signal,options.signal].filter((s): s is AbortSignal=>!!s);
    const abort=()=>controller.abort();inputs.forEach(signal=>signal.aborted?abort():signal.addEventListener('abort',abort,{once:true}));
    try { current(controller.signal);const value=await this.request<T>(`/profiles/${encodeURIComponent(this.profileId)}/${path}`,{...options,signal:controller.signal});current(controller.signal);return value; }
    finally {inputs.forEach(signal=>signal.removeEventListener('abort',abort));}
  }
  async addons(signal: AbortSignal, refresh=false): Promise<AddonInstallation[]> {
    current(signal);current(this.lifetime.signal);
    if (refresh) this.installations=undefined;
    if (!this.installations) {
      const pending=this.fetch<{addons:AddonInstallation[]}>('addons').then(result=>{
        if (!Array.isArray(result.addons) || result.addons.length>100) throw Error('Your addon list could not be read.');
        return [cinemeta(),...result.addons.filter(a=>a.id!==CINEMETA_ID && a.policy?.status==='allowed')].sort((a,b)=>a.position-b.position);
      });
      this.installations=pending;
      void pending.catch(()=>{if(this.installations===pending)this.installations=undefined;});
    }
    const value=await this.installations;current(signal);current(this.lifetime.signal);return value;
  }
  async choices(signal: AbortSignal, refresh=false) { return catalogChoices(await this.addons(signal,refresh)); }
  private resource<T>(addonId: string, resource: 'catalog'|'meta', type:string, id:string, extra:Record<string,string>, signal:AbortSignal) {
    return this.fetch<T>(addonId===CINEMETA_ID?'catalogs/cinemeta':`addons/${encodeURIComponent(addonId)}/resource`,
      {method:'POST',body:JSON.stringify({resource,type,id,extra}),signal});
  }
  async catalog(choice:CatalogChoice, extra:Record<string,string>, signal:AbortSignal) {
    const response=await this.resource<{metas:unknown;received?:number}>(choice.addonId,'catalog',choice.catalog.type,choice.catalog.id,extra,signal);
    return addonCatalogPage(response);
  }
  async metadata(initial:Meta, signal:AbortSignal, preferredAddon?:string, requireFresh=false): Promise<Meta> {
    preferredAddon ||= initial.sourceAddonId;
    const addons=(await this.addons(signal)).filter(a=>a.enabled&&!a.manifest.behaviorHints?.configurationRequired);
    const matching:AddonInstallation[]=[];
    for (const addon of addons) { current(signal);current(this.lifetime.signal);if(await this.supports(addon,initial,signal))matching.push(addon); }
    current(signal);current(this.lifetime.signal);
    // Prefer the clicked catalog's metadata provider, then configured addons,
    // with built-in metadata last. Resource-declared types/prefixes still apply.
    matching.sort((a,b)=>(a.id===preferredAddon?-2:a.id===CINEMETA_ID?1:0)-(b.id===preferredAddon?-2:b.id===CINEMETA_ID?1:0));
    let last: unknown, best: Meta | undefined;
    for (const addon of matching) {
      current(signal);current(this.lifetime.signal);
      try { const result=await this.resource<{meta:unknown}>(addon.id,'meta',initial.type,initial.id,{},signal);
        if (result.meta===null) continue;
        const meta=contentMeta(result.meta);
        if(meta.id!==initial.id||meta.type!==initial.type)throw Error('This addon returned details for a different title.');
        if (meta.type !== 'series' || meta.videos?.length) return meta;
        best ??= meta;
      } catch(error) { current(signal);current(this.lifetime.signal);last=error; }
    }
    if (best) return fallbackVideos(best, contentMeta(initial));
    if (last) throw last;
    if (requireFresh) throw Error('This channel addon did not return a fresh schedule.');
    return contentMeta(initial);
  }
  async searchHistory(signal:AbortSignal) {
    const rows=await this.fetch<{query:string}[]>('search-history',{signal});
    if (!Array.isArray(rows)||rows.length>30)throw Error('Recent searches could not be read.');
    return [...new Set(rows.flatMap(row=>typeof row?.query==='string'&&row.query.trim()&&row.query.length<=200&&!/[\x00-\x1f]/.test(row.query)?[row.query]:[]))];
  }
  async rememberSearch(query:string,signal:AbortSignal) {
    query=query.trim();if(!query||query.length>200||/[\x00-\x1f]/.test(query))throw Error('Enter a title of up to 200 characters.');
    await this.fetch('search-history',{method:'POST',body:JSON.stringify({query}),signal});
  }
  async clearSearchHistory(signal:AbortSignal) { await this.fetch('search-history',{method:'DELETE',signal}); }
}
